import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { adminApi } from "../lib/adminAuth";
import ConfirmModal from "./ConfirmModal";

const CATS = [
  "Reflexión", "Rutina", "Personas", "Tiempo", "Memoria",
  "Lecturas", "Ciudad", "Trabajo", "Naturaleza", "Notas rápidas",
];

const EMPTY = {
  slug: "", titulo: "", excerpt: "", contenido_md: "", imagen_url: "",
  alt_text: "", categoria: "Reflexión", tiempo_lectura: 5, destacado: false,
};

function slugify(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n").replace(/Ñ/g, "N")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function PanelEntradas() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  // Track whether the user manually edited the slug (so we don't overwrite it)
  const slugTouchedRef = useRef(false);

  const load = () => adminApi.get("/admin/posts").then((r) => setPosts(r.data));

  useEffect(() => { load(); }, []);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2400);
  };

  const startEdit = (p) => {
    setEditing(p.slug);
    slugTouchedRef.current = true; // editing existing, never auto-rewrite
    setForm({
      slug: p.slug, titulo: p.titulo, excerpt: p.excerpt, contenido_md: p.contenido_md,
      imagen_url: p.imagen_url, alt_text: p.alt_text || "", categoria: p.categoria,
      tiempo_lectura: p.tiempo_lectura, destacado: p.destacado,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onTitleChange = (value) => {
    setForm((f) => {
      const next = { ...f, titulo: value };
      // Auto-fill slug only when creating a new post AND user hasn't touched slug
      if (!editing && !slugTouchedRef.current) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const onSlugChange = (value) => {
    slugTouchedRef.current = true;
    // Always normalize as user types — prevents bad slugs entering DB
    setForm((f) => ({ ...f, slug: slugify(value) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        // eslint-disable-next-line no-unused-vars
        const { slug, ...rest } = form;
        await adminApi.put(`/admin/posts/${editing}`, rest);
        showToast("Entrada actualizada");
      } else {
        await adminApi.post("/admin/posts", form);
        showToast("Entrada creada");
      }
      setEditing(null);
      setForm(EMPTY);
      slugTouchedRef.current = false;
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Error", "error");
    }
  };

  const toggleFeatured = async (slug) => {
    try {
      await adminApi.patch(`/admin/posts/${slug}/destacado`);
      load();
    } catch {
      showToast("No se pudo cambiar", "error");
    }
  };

  const askDelete = (p) =>
    setConfirm({
      title: "¿Borrar entrada?",
      message: `Vas a eliminar definitivamente "${p.titulo}". Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/posts/${p.slug}`);
          showToast("Entrada borrada");
          load();
        } catch {
          showToast("No se pudo borrar", "error");
        }
        setConfirm(null);
      },
    });

  return (
    <div data-testid="panel-entradas">
      <div className="panel-eyebrow">Contenido</div>
      <h1>Entradas</h1>

      <div className="editor-grid" style={{ marginTop: 24 }}>
        <div className="panel-card">
          <h3>{editing ? `Editando: ${editing}` : "Nueva entrada"}</h3>
          <form onSubmit={submit} data-testid="entrada-form">
            <input
              placeholder="slug (ej: mi-entrada)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              disabled={!!editing}
              required
              data-testid="entrada-slug"
            />
            <input
              placeholder="Título"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              required
              data-testid="entrada-titulo"
            />
            <input
              placeholder="Excerpt"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              required
              data-testid="entrada-excerpt"
            />

            <input
              placeholder="URL de imagen de portada"
              value={form.imagen_url}
              onChange={(e) => setForm({ ...form, imagen_url: e.target.value })}
              required
              data-testid="entrada-imagen-url"
            />
            {form.imagen_url && (
              <div className="image-preview" data-testid="entrada-image-preview">
                <img src={form.imagen_url} alt={form.alt_text || "Preview"} />
              </div>
            )}
            <input
              placeholder="Alt text de imagen"
              value={form.alt_text}
              onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              data-testid="entrada-alt-text"
            />

            <select
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              data-testid="entrada-categoria"
            >
              {CATS.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <input
              type="number" min="1" max="40"
              placeholder="Tiempo de lectura (min)"
              value={form.tiempo_lectura}
              onChange={(e) => setForm({ ...form, tiempo_lectura: Number(e.target.value) })}
              data-testid="entrada-tiempo"
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 12px" }}>
              <input
                type="checkbox"
                checked={form.destacado}
                onChange={(e) => setForm({ ...form, destacado: e.target.checked })}
                data-testid="entrada-destacado"
                style={{ width: "auto", marginBottom: 0 }}
              />
              Marcar como destacada
            </label>
            <textarea
              className="admin-card-textarea"
              placeholder="Contenido en markdown…"
              value={form.contenido_md}
              onChange={(e) => setForm({ ...form, contenido_md: e.target.value })}
              required
              data-testid="entrada-contenido"
              style={{
                width: "100%", minHeight: 220, padding: "10px 12px",
                border: "1px solid var(--border-md)", borderRadius: "var(--radius)",
                fontFamily: "ui-monospace,Menlo,monospace", fontSize: "0.88rem",
                background: "var(--bg)", color: "var(--ink)",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary btn-sm" data-testid="entrada-submit">
                {editing ? "Actualizar" : "Publicar"}
              </button>
              {editing && (
                <button type="button" className="btn btn-outline btn-sm"
                  onClick={() => { setEditing(null); setForm(EMPTY); slugTouchedRef.current = false; }}>
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </div>

        <div>
          {form.contenido_md && (
            <div className="panel-card" style={{ marginBottom: 24 }}>
              <h3>Vista previa</h3>
              <div style={{
                fontSize: "1rem", lineHeight: 1.7,
                maxHeight: 400, overflowY: "auto",
                padding: 16, background: "var(--bg)",
                borderRadius: "var(--radius)", border: "1px solid var(--border)",
              }}>
                <ReactMarkdown>{form.contenido_md}</ReactMarkdown>
              </div>
            </div>
          )}
          <div className="panel-card">
            <h3>Todas las entradas ({posts.length})</h3>
            <table className="panel-table" data-testid="entradas-list">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Categoría</th>
                  <th>Vistas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.slug}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {p.destacado && <span style={{ color: "var(--accent)" }}>★ </span>}
                        {p.titulo}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "var(--ink-3)" }}>{p.slug}</div>
                    </td>
                    <td>{p.categoria}</td>
                    <td>{(p.vistas || 0).toLocaleString("es-ES")}</td>
                    <td>
                      <div className="panel-actions">
                        <button
                          className={`btn-tiny ${p.destacado ? "success" : ""}`}
                          onClick={() => toggleFeatured(p.slug)}
                          data-testid={`entrada-featured-${p.slug}`}
                        >
                          {p.destacado ? "★ Destacada" : "☆ Destacar"}
                        </button>
                        <button className="btn-tiny" onClick={() => startEdit(p)} data-testid={`entrada-edit-${p.slug}`}>
                          Editar
                        </button>
                        <button className="btn-tiny danger" onClick={() => askDelete(p)} data-testid={`entrada-delete-${p.slug}`}>
                          Borrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      {toast && <div className={`toast ${toast.type === "error" ? "error" : ""}`}>{toast.msg}</div>}
    </div>
  );
}
